import { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'wouter';

import { SignupFlow } from '../features/auth/SignupFlow.js';
import { useSession } from '../features/auth/session.js';
import { registrarDiaDeUso } from '../features/install/install.js';
import { ClubWall } from '../features/invite/ClubWall.js';
import { RequestAccess } from '../features/access/RequestAccess.js';
import { AdminPanel } from '../features/admin/AdminPanel.js';
import { PactScreen } from '../features/pact/PactScreen.js';
import { InviteLanding } from '../features/invite/InviteLanding.js';
import { MapScreen } from '../features/map/MapScreen.js';
import { AddPlace } from '../features/places/AddPlace.js';
import { MyPlaces } from '../features/places/MyPlaces.js';
import { Profile } from '../features/profile/Profile.js';
import { PlaceDetail } from '../features/visit/PlaceDetail.js';
import { SplashIntro } from '../features/splash/SplashIntro.js';
import { BottomNav, type Destino } from '../shared/ui/BottomNav.js';
import { OfflineScreen } from '../shared/ui/OfflineScreen.js';
import { ESPACIO_NAV } from '../shared/ui/primitives.js';
import { fetchMe } from '../shared/lib/api.js';
import {
  checkConnection,
  type ConnectionState,
} from '../shared/lib/connectivity.js';
import { useOnlineStatus } from '../shared/lib/useOnlineStatus.js';

/**
 * Shell de la aplicación.
 *
 * ADR-006: sin conexión no hay app, hay pantalla de sin conexión.
 * ADR-007: durante la Era Pionera el mapa vacío es el estado ESPERADO, no
 * un incidente — y se distingue de un fallo de carga, que sí lo es.
 * ADR-011: la raíz SIN código muestra la pantalla de club, no el mapa.
 */
export function App() {
  /**
   * El splash va ENVOLVIENDO la app, no bloqueándola: mientras se ve, la
   * sesión ya se está restaurando por debajo. Así los 3 segundos de marca
   * no se suman al arranque — se solapan con él.
   */
  const [splashListo, setSplashListo] = useState(false);
  const browserOnline = useOnlineStatus();
  const status = useSession((s) => s.status);
  const setProfile = useSession((s) => s.setProfile);

  const [connection, setConnection] = useState<ConnectionState | null>(null);
  const [checking, setChecking] = useState(false);

  const verify = useCallback(async () => {
    setChecking(true);
    const estado = await checkConnection();
    setConnection(estado);
    setChecking(false);
  }, []);

  useEffect(() => {
    void verify();

    /*
     * Cuenta este día para el momento de respaldo del prompt de
     * instalación (ADR-015): al tercer día DISTINTO se vuelve a ofrecer,
     * una sola vez. Se hace al arrancar y no al mostrar la tarjeta —
     * cuando la tarjeta se muestre, el conteo tiene que llevar días
     * hecho.
     */
    registrarDiaDeUso(new Date().toISOString().slice(0, 10));

    /**
     * Restaura la sesión del servidor (GU-AUTH-06).
     *
     * Hasta hoy aquí había un `setStatus('anonymous')` hardcodeado, así
     * que la cookie de 30 días existía pero nadie seguía conectado al
     * volver a abrir. `fetchMe` devuelve `null` sin sesión —eso no es un
     * error— y ante un fallo de red cae también a anónimo: dejar el
     * estado en `loading` colgaría la app en blanco en vez de mostrar la
     * pantalla de sin conexión.
     */
    void fetchMe()
      .then((perfil) => setProfile(perfil))
      .catch(() => setProfile(null));
  }, [verify, setProfile]);

  useEffect(() => {
    if (browserOnline) void verify();
    else setConnection({ status: 'offline', problem: 'no_network' });
  }, [browserOnline, verify]);

  /**
   * El splash va ENCIMA de todo y ANTES de cualquier `return` temprano.
   *
   * Estuvo montado después del `return <Cargando/>` de la restauración de
   * sesión: mientras la app arrancaba —que es justo cuando debe verse— el
   * splash ni existía, y cuando se montaba el momento ya había pasado. En
   * producción no aparecía nunca.
   *
   * Como es `position: fixed`, tapa lo que haya debajo sin bloquearlo: la
   * sesión se restaura mientras se ve, así que los 3 segundos de marca se
   * solapan con el arranque en vez de sumarse.
   */
  const splash = !splashListo ? (
    <SplashIntro onDone={() => setSplashListo(true)} />
  ) : null;

  if (connection?.status === 'offline') {
    return (
      <>
        {splash}
          <OfflineScreen
          problem={connection.problem}
          onRetry={() => void verify()}
          retrying={checking}
        />
      </>
    );
  }

  // Mientras no se sepa si hay sesión, no se decide qué pantalla toca:
  // enseñar el club a alguien que sí tiene cuenta y quitárselo medio
  // segundo después es peor que esperar ese medio segundo.
  if (status === 'loading')
    return (
      <>
        {splash}
        <Cargando />
      </>
    );

  return (
    <>
      {splash}
      <Switch>
        <Route path="/i/:code">{(p) => <RutaInvitacion code={p.code!} />}</Route>
      <Route path="/entrar">
        {status === 'authenticated' ? <Mapa /> : <RutaInvitacion code={null} />}
      </Route>
      <Route path="/agregar">
        {status === 'authenticated' ? <RutaAgregar /> : <RutaClub />}
      </Route>
      <Route path="/mis-lugares">
        {status === 'authenticated' ? <RutaMisLugares /> : <RutaClub />}
      </Route>
      <Route path="/perfil">
        {status === 'authenticated' ? <RutaPerfil /> : <RutaClub />}
      </Route>
      <Route path="/lugar/:id">
        {(p) =>
          status === 'authenticated' ? <RutaLugar id={p.id!} /> : <RutaClub />
        }
      </Route>
      <Route path="/solicitar">
        {/*
         * La segunda puerta (ADR-021). Pública: quien pide todavía no tiene
         * cuenta, y exigir sesión aquí sería la definición de callejón.
         */}
        {status === 'authenticated' ? <Mapa /> : <RutaSolicitar />}
      </Route>
      <Route path="/admin">
        {/*
         * El panel (ADR-024). Sin sesión va al club; CON sesión, quien no sea
         * admin recibe un 404 de la API y ve "por aquí no hay nada".
         *
         * No se comprueba el rol aquí: hacerlo en el cliente pondría en el
         * bundle la lista de lo que existe, y el 404 del servidor dejaría de
         * ser el único que decide.
         */}
        {status === 'authenticated' ? <RutaAdmin /> : <RutaClub />}
      </Route>
        <Route>{status === 'authenticated' ? <Mapa /> : <RutaClub />}</Route>
      </Switch>

      {/*
       * La barra solo existe con sesión. Sin ella, sus tres destinos
       * llevan a la pantalla de club: una navegación que siempre devuelve
       * al mismo sitio es peor que no tenerla.
       *
       * Fuera del `Switch` para que no se remonte al cambiar de ruta —
       * remontarla haría parpadear el estado activo en cada navegación.
       */}
      {status === 'authenticated' && <NavPrincipal />}
    </>
  );
}

/**
 * La navegación inferior, con el destino activo derivado de la ruta.
 *
 * `/agregar` no es una pestaña: es un flujo con su propia salida, y
 * marcarlo activo mentiría sobre dónde está el usuario. Ahí no se ilumina
 * ninguna.
 */
function NavPrincipal() {
  const [ruta, navegar] = useLocation();

  const activo: Destino =
    ruta === '/mis-lugares' || ruta === '/perfil' ? ruta : '/';

  return <BottomNav activo={activo} onNavegar={navegar} />;
}

/**
 * La landing y, tras el tap explícito, el alta.
 *
 * El pase NO se fija al abrir (spec §4): si lo hiciera, un tercero podría
 * mandarte a `/i/SU_CODIGO` —navegación permitida por `SameSite=Lax`— y
 * acabarías registrado bajo su árbol, dándole crédito de `invitesUsed`
 * sin haberlo decidido.
 */
function RutaInvitacion({ code }: { code: string | null }) {
  const [aceptado, setAceptado] = useState<string | null>(null);
  /**
   * El Pacto va ENTRE la landing y el alta (ADR-028): se acepta **antes de
   * gastar el pase**. Si fuera después, alguien podría quedar dentro sin
   * haberlo visto, y sancionarlo luego sería injusto.
   *
   * Y rechazarlo NO quema el pase: `pactoVisto` vuelve a false y el código
   * sigue vivo hasta su fecha. Una puerta que castiga por leer y dudar
   * deslegitima el pacto entero.
   */
  const [pactoAceptado, setPactoAceptado] = useState(false);
  const setProfile = useSession((s) => s.setProfile);
  const [, navegar] = useLocation();

  if (aceptado && !pactoAceptado) {
    return (
      <PactScreen
        puerta="invitacion"
        onAceptar={() => setPactoAceptado(true)}
        onRechazar={() => setAceptado(null)}
      />
    );
  }

  if (aceptado || code === null) {
    return (
      <SignupFlow
        inviteCode={aceptado ?? ''}
        onDone={(perfil) => {
          setProfile(perfil);
          navegar('/');
        }}
      />
    );
  }

  return <InviteLanding code={code} onAccept={setAceptado} />;
}

/**
 * La ficha del lugar, con los cuatro estados de docs/03 §4.3.
 *
 * Exige sesión igual que agregar o el perfil: ver un lugar es público en la
 * API —una ficha se comparte por WhatsApp— pero durante la Era Pionera el
 * Modo Explorador sigue suspendido (ADR-011), así que quien llegue sin
 * cuenta ve el club. Cuando el Modo Explorador vuelva, esta ruta es de las
 * primeras que deja de necesitar sesión.
 */
function RutaLugar({ id }: { id: string }) {
  const [, navegar] = useLocation();
  return <PlaceDetail placeId={id} onVolver={() => navegar('/mis-lugares')} />;
}

/**
 * Agregar lugar. Al terminar va a la FICHA del lugar recién puesto, no a la
 * lista: quien acaba de poner un pin está parado ahí, y es el único momento
 * en que puede empezar su visita sin volver a caminar. Mandarlo a "mis
 * lugares" le hacía leer que le falta la opinión y no le daba dónde darla.
 */
function RutaAgregar() {
  const [, navegar] = useLocation();
  return (
    <AddPlace
      onDone={(place) => navegar(`/lugar/${place.id}`)}
      onCancel={() => navegar('/')}
      /*
       * "Ver ese lugar" ante un duplicado lleva a "mis lugares", no al
       * mapa. El mapa solo muestra lo que hay a 5 km de donde estás, así
       * que si el duplicado es de otra zona la persona acaba en una
       * pantalla vacía creyendo que su lugar se perdió — que es lo que
       * pasó en la primera prueba real.
       */
      onSeePlace={() => navegar('/mis-lugares')}
    />
  );
}

/**
 * "Tus lugares". Cada lugar abre SU ficha, que es donde se opina.
 *
 * `MyPlaces` ya renderizaba cada fila como un botón, pero aquí no se le
 * pasaba el `onOpenPlace`: existía el afordance y no la navegación. Sin esto,
 * la ficha solo se alcanzaba recién creado el lugar o tecleando la URL — o
 * sea, la pantalla donde se publica una opinión no tenía camino desde la app.
 */
function RutaMisLugares() {
  const [, navegar] = useLocation();
  return (
    <MyPlaces
      onAddPlace={() => navegar('/agregar')}
      onOpenPlace={(id) => navegar(`/lugar/${id}`)}
    />
  );
}

/**
 * El perfil. Si la sesión todavía no trajo el objeto —hay estado
 * `authenticated` un instante antes de que llegue— se espera en vez de
 * renderizar una pantalla a medias.
 */
function RutaPerfil() {
  const [, navegar] = useLocation();
  const perfil = useSession((s) => s.profile);
  const setProfile = useSession((s) => s.setProfile);

  if (!perfil) return <Cargando />;

  return (
    <Profile
      perfil={perfil}
      onGuardado={setProfile}
      onVolver={() => navegar('/')}
    />
  );
}

function RutaClub() {
  const [, navegar] = useLocation();
  return (
    <ClubWall
      onHasAccount={() => navegar('/entrar')}
      onPedirEntrar={() => navegar('/solicitar')}
    />
  );
}

function RutaSolicitar() {
  const [, navegar] = useLocation();
  return <RequestAccess onVolver={() => navegar('/')} />;
}

function RutaAdmin() {
  const [, navegar] = useLocation();
  return <AdminPanel onVolver={() => navegar('/')} />;
}

function Cargando() {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--gu-surface-app)',
      }}
    >
      <p style={{ color: 'var(--gu-text-secondary)' }}>Un momento…</p>
    </main>
  );
}

/**
 * El mapa. Solo para quien tiene sesión: durante la Era Pionera el Modo
 * Explorador está suspendido (ADR-011).
 *
 * El cuerpo vive en `MapScreen`; aquí queda la cabecera y la navegación.
 */
function Mapa() {
  const [, navegar] = useLocation();
  const perfil = useSession((s) => s.profile);

  return (
    <main
      style={{
        /*
         * `height` y NO `minHeight` como en las demás pantallas.
         *
         * `MapCanvas` pide `height: 100%`, y un porcentaje necesita que la
         * cadena de ancestros tenga altura DEFINIDA. Con `min-height` el
         * `flex: 1` de `MapScreen` no la tiene, así que el mapa colapsaba
         * a 0 px: el canvas existía, los pines existían, y no se veía
         * nada. Medido en el navegador, no supuesto.
         *
         * Además es lo correcto aquí: el mapa ocupa el viewport exacto y
         * no hace scroll de página — el desplazamiento vive dentro del
         * mapa.
         */
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--gu-surface-sunken)',
        // Reserva el hueco de la barra fija de navegación.
        paddingBottom: ESPACIO_NAV,
      }}
    >
      {/*
       * El enlace "Tus lugares" que vivía aquí se fue a la barra inferior:
       * era la única forma de llegar, y un enlace de texto escondido en la
       * cabecera no es navegación.
       */}
      <header
        style={{
          padding: 'var(--gu-space-4)',
          background: 'var(--gu-surface-app)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--gu-space-3)',
          flexShrink: 0,
        }}
      >
        <div>
          <h1 style={{ margin: 0, lineHeight: 0 }}>
            <img
              src="/brand/logo-horizontal.png"
              alt="Gourmet Urbano"
              style={{ height: '1.75rem', width: 'auto', display: 'block' }}
            />
          </h1>
          <p
            style={{
              margin: `var(--gu-space-1) 0 0`,
              fontSize: 'var(--gu-text-caption)',
              color: 'var(--gu-text-secondary)',
            }}
          >
            Modo Gourmetero
          </p>
        </div>

        {/*
         * El avatar como atajo al perfil. Es la convención que la gente ya
         * conoce, y de paso confirma con quién está firmando lo que
         * publique — que es justo lo que GU-AUTH-04 quiere que sea visible.
         */}
        {perfil?.avatarUrl && (
          <button
            type="button"
            onClick={() => navegar('/perfil')}
            aria-label="Tu perfil"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              lineHeight: 0,
              minWidth: 'var(--gu-touch-min)',
              minHeight: 'var(--gu-touch-min)',
            }}
          >
            <img
              src={perfil.avatarUrl}
              alt=""
              width={40}
              height={40}
              style={{ borderRadius: '50%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        )}
      </header>

      <MapScreen
        onAddPlace={() => navegar('/agregar')}
        onOpenPlace={(id) => navegar(`/lugar/${id}`)}
      />
    </main>
  );
}
